// src/pages/Recovery.tsx - Modern Redesign
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  submitRecovery,
  getRecoveryRequests,
} from "../redux/slices/recoverySlice";
import { getUnreadChatCount } from "../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Search,
  MessageSquare,
  Filter,
  Plus,
  Wallet as WalletIcon,
  Hash,
  ExternalLink,
  Copy,
  Check,
  Eye,
  X,
  Bell,
  Calendar,
  Sparkles,
  ChevronDown,
  Zap,
  Target,
  Users,
  BarChart3,
  ShieldCheck,
} from "lucide-react";
import toast from "react-hot-toast";
import RecoveryChat from "../components/recovery/RecoveryChat";
import { formatCrypto, formatCurrency } from "../utils/currency";
import LoadingScreen from "../components/LoadingScreen";

interface RecoveryRequest {
  id: number;
  case_number: string;
  user_id: number;
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  status: string;
  recovery_fee: number | null;
  success_fee_percentage: number;
  estimated_recovery_amount: number | null;
  recovery_plan: string | null;
  additional_notes: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  assessment_completed_at?: string;
  recovery_started_at?: string;
  recovery_completed_at?: string;
  estimated_completion_date?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    verification_level: number;
  };
  unread_message_count?: number;
}

const Recovery: React.FC = () => {
  const dispatch = useDispatch();
  const { requests, isLoading } = useSelector(
    (state: RootState) => state.recovery,
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    wallet_address: "",
    transaction_id: "",
    amount: "",
    currency: "BTC",
    network: "Bitcoin",
    additional_notes: "",
  });

  const [filter, setFilter] = useState({
    search: "",
    status: "",
    network: "",
    date_from: "",
    date_to: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedCase, setSelectedCase] = useState<RecoveryRequest | null>(
    null,
  );
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showChatModal, setShowChatModal] = useState<{
    isOpen: boolean;
    caseId: number;
    caseNumber: string;
  }>({
    isOpen: false,
    caseId: 0,
    caseNumber: "",
  });

  const currencyOptions = [
    {
      value: "BTC",
      label: "Bitcoin (BTC)",
      networks: ["Bitcoin", "Lightning"],
    },
    {
      value: "ETH",
      label: "Ethereum (ETH)",
      networks: ["Ethereum", "Ethereum 2.0"],
    },
    {
      value: "USDT",
      label: "Tether (USDT)",
      networks: ["Ethereum", "Tron", "Solana", "BNB Smart Chain"],
    },
    {
      value: "BNB",
      label: "BNB (BNB)",
      networks: ["BNB Smart Chain", "BNB Beacon Chain"],
    },
    { value: "XRP", label: "Ripple (XRP)", networks: ["XRP Ledger"] },
    { value: "ADA", label: "Cardano (ADA)", networks: ["Cardano"] },
    { value: "SOL", label: "Solana (SOL)", networks: ["Solana"] },
    { value: "DOT", label: "Polkadot (DOT)", networks: ["Polkadot"] },
    { value: "DOGE", label: "Dogecoin (DOGE)", networks: ["Dogecoin"] },
    { value: "TRX", label: "TRON (TRX)", networks: ["TRON"] },
    { value: "MATIC", label: "Polygon (MATIC)", networks: ["Polygon"] },
    { value: "LTC", label: "Litecoin (LTC)", networks: ["Litecoin"] },
    { value: "SHIB", label: "Shiba Inu (SHIB)", networks: ["Ethereum"] },
    {
      value: "AVAX",
      label: "Avalanche (AVAX)",
      networks: ["Avalanche C-Chain", "Avalanche X-Chain", "Avalanche P-Chain"],
    },
    { value: "LINK", label: "Chainlink (LINK)", networks: ["Ethereum"] },
    { value: "UNI", label: "Uniswap (UNI)", networks: ["Ethereum"] },
    { value: "ATOM", label: "Cosmos (ATOM)", networks: ["Cosmos"] },
    { value: "XMR", label: "Monero (XMR)", networks: ["Monero"] },
  ];

  const getNetworkOptions = () => {
    const selectedCurrency = currencyOptions.find(
      (c) => c.value === formData.currency,
    );
    return selectedCurrency ? selectedCurrency.networks : ["Bitcoin"];
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value;
    const currency = currencyOptions.find((c) => c.value === newCurrency);
    const defaultNetwork = currency ? currency.networks[0] : "Bitcoin";

    setFormData({
      ...formData,
      currency: newCurrency,
      network: defaultNetwork,
    });
  };

  useEffect(() => {
    dispatch(getRecoveryRequests({}) as any);
    if (user) {
      dispatch(getUnreadChatCount() as any);
    }
  }, [dispatch, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.wallet_address ||
      !formData.transaction_id ||
      !formData.amount
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      await dispatch(
        submitRecovery({
          ...formData,
          amount: parseFloat(formData.amount),
        }) as any,
      );
      toast.success("Recovery request submitted successfully!");
      setShowForm(false);
      setFormData({
        wallet_address: "",
        transaction_id: "",
        amount: "",
        currency: "BTC",
        network: "Bitcoin",
        additional_notes: "",
      });
    } catch (error) {
      toast.error("Failed to submit recovery request");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case "in_progress":
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-rose-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500 animate-pulse" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800";
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800";
      case "failed":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800";
      case "cancelled":
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700";
      default:
        return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const filteredRequests = requests.filter((request: RecoveryRequest) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return (
        request.case_number?.toLowerCase().includes(searchLower) ||
        request.wallet_address?.toLowerCase().includes(searchLower) ||
        request.transaction_id?.toLowerCase().includes(searchLower) ||
        request.currency?.toLowerCase().includes(searchLower)
      );
    }

    if (filter.status && request.status !== filter.status) {
      return false;
    }

    if (filter.network && request.network !== filter.network) {
      return false;
    }

    if (filter.date_from || filter.date_to) {
      const createdDate = new Date(request.created_at);
      const fromDate = filter.date_from ? new Date(filter.date_from) : null;
      const toDate = filter.date_to ? new Date(filter.date_to) : null;

      if (fromDate && createdDate < fromDate) return false;
      if (toDate && createdDate > toDate) return false;
    }

    return true;
  });

  const totalUnreadMessages = requests.reduce(
    (total: number, request: RecoveryRequest) =>
      total + (request.unread_message_count || 0),
    0,
  );

  const casesWithUnreadMessages = requests.filter(
    (request: RecoveryRequest) => (request.unread_message_count || 0) > 0,
  ).length;

  const calculateStatistics = () => {
    const totalCases = requests.length;
    const activeCases = requests.filter((r: RecoveryRequest) =>
      [
        "submitted",
        "under_review",
        "assessment_complete",
        "recovery_plan_sent",
        "recovery_approved",
        "in_progress",
      ].includes(r.status),
    ).length;

    const completedCases = requests.filter(
      (r: RecoveryRequest) => r.status === "completed",
    ).length;

    const totalAmount = requests.reduce(
      (sum: number, r: RecoveryRequest) => sum + r.amount,
      0,
    );
    const estimatedRecovery = requests.reduce(
      (sum: number, r: RecoveryRequest) =>
        sum + (r.estimated_recovery_amount || 0),
      0,
    );

    const successRate =
      totalCases > 0 ? Math.round((completedCases / totalCases) * 100) : 0;

    return {
      totalCases,
      activeCases,
      completedCases,
      totalAmount,
      estimatedRecovery,
      successRate,
      totalUnreadMessages,
      casesWithUnreadMessages,
    };
  };

  const stats = calculateStatistics();

  const openCaseDetails = (caseItem: RecoveryRequest) => {
    setSelectedCase(caseItem);
    setShowCaseDetails(true);
  };

  const closeCaseDetails = () => {
    setSelectedCase(null);
    setShowCaseDetails(false);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`Copied ${field} to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const getExplorerUrl = (network: string, transactionId: string) => {
    const explorers: Record<string, string> = {
      Bitcoin: `https://blockchain.info/tx/${transactionId}`,
      Ethereum: `https://etherscan.io/tx/${transactionId}`,
      "Binance Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      BSC: `https://bscscan.com/tx/${transactionId}`,
      Solana: `https://solscan.io/tx/${transactionId}`,
      Polygon: `https://polygonscan.com/tx/${transactionId}`,
      Arbitrum: `https://arbiscan.io/tx/${transactionId}`,
      "BNB Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      TRON: `https://tronscan.org/#/transaction/${transactionId}`,
      Cardano: `https://cardanoscan.io/transaction/${transactionId}`,
      Litecoin: `https://blockchair.com/litecoin/transaction/${transactionId}`,
      Dogecoin: `https://blockchair.com/dogecoin/transaction/${transactionId}`,
    };
    return (
      explorers[network] || `https://blockchair.com/search?q=${transactionId}`
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const clearFilters = () => {
    setFilter({
      search: "",
      status: "",
      network: "",
      date_from: "",
      date_to: "",
    });
  };

  if (!user || user.verification_level < 3) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Fund Recovery
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Recover lost or stolen cryptocurrency funds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* KYC Required */}
        <div className={`${GlobalStyles.card}`}>
          <div className="text-center py-12">
            <div className="relative mx-auto w-20 h-20 mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 to-rose-500/10 rounded-full blur-lg"></div>
              <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                <Shield className="h-10 w-10 text-rose-500" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              KYC Verification Required
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Complete KYC Level 3 verification to access advanced recovery
              services.
            </p>
            <button
              onClick={() => (window.location.href = "/profile")}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/kyc`}
            >
              <Shield className="h-5 w-5 mr-2 transition-transform group-hover/kyc:scale-110" />
              <span>Complete KYC Verification</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && requests.length === 0) {
    return <LoadingScreen message="Loading recovery cases..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Fund Recovery
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-600 dark:text-gray-400">
                    Recover lost or stolen cryptocurrency funds
                  </p>
                  {stats.totalUnreadMessages > 0 && (
                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-rose-500 to-rose-600 text-white text-xs px-2.5 py-1 rounded-full shadow-sm">
                      <Bell className="h-3 w-3" />
                      {stats.totalUnreadMessages} unread
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/new-case`}
            >
              <Plus className="h-5 w-5 mr-2 transition-transform group-hover/new-case:rotate-90" />
              <span>New Recovery Request</span>
              <Sparkles className="h-5 w-5 ml-2 opacity-0 group-hover/new-case:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Cases */}
        <div
          className={`${GlobalStyles.card} group/stat hover:border-primary/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Total Cases
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalCases}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium text-blue-500">
                  {stats.activeCases}
                </span>{" "}
                active cases
              </p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/10 group-hover/stat:scale-110 transition-transform">
              <FileText className="h-6 w-6 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Success Rate */}
        <div
          className={`${GlobalStyles.card} group/stat hover:border-emerald-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Success Rate
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.successRate}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium text-emerald-500">
                  {stats.completedCases}
                </span>{" "}
                recovered
              </p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/10 group-hover/stat:scale-110 transition-transform">
              <TrendingUp className="h-6 w-6 text-emerald-500" />
            </div>
          </div>
        </div>

        {/* Recovered Amount */}
        <div
          className={`${GlobalStyles.card} group/stat hover:border-purple-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Recovered
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats.estimatedRecovery * 45000)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Estimated value
              </p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/10 group-hover/stat:scale-110 transition-transform">
              <DollarSign className="h-6 w-6 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Unread Messages */}
        <div
          className={`${GlobalStyles.card} group/stat hover:border-rose-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Unread Messages
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalUnreadMessages}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium text-rose-500">
                  {stats.casesWithUnreadMessages}
                </span>{" "}
                cases need attention
              </p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-rose-500/20 to-rose-500/10 group-hover/stat:scale-110 transition-transform">
              <Bell className="h-6 w-6 text-rose-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Recovery Form */}
      {showForm && (
        <div className={`${GlobalStyles.card} relative overflow-hidden`}>
          {/* Background Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

          {/* Form Content */}
          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  New Recovery Request
                </h3>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Wallet Address */}
                <div className="group">
                  <label className={`${GlobalStyles.inputLabel}`}>
                    Wallet Address
                  </label>
                  <div className="relative">
                    <WalletIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.wallet_address}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          wallet_address: e.target.value,
                        })
                      }
                      className={GlobalStyles.input}
                      placeholder="Enter the wallet address where funds were sent..."
                      required
                    />
                  </div>
                </div>

                {/* Transaction ID */}
                <div className="group">
                  <label className={`${GlobalStyles.inputLabel}`}>
                    Transaction ID/Hash
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.transaction_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          transaction_id: e.target.value,
                        })
                      }
                      className={GlobalStyles.input}
                      placeholder="Enter the transaction hash/ID..."
                      required
                    />
                  </div>
                </div>

                {/* Amount */}
                <div className="group">
                  <label className={`${GlobalStyles.inputLabel}`}>
                    Amount Lost
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.00000001"
                      min="0.00000001"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      className={GlobalStyles.input}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="group">
                  <label className={`${GlobalStyles.inputLabel}`}>
                    Currency
                  </label>
                  <div className="relative">
                    <select
                      value={formData.currency}
                      onChange={handleCurrencyChange}
                      className={GlobalStyles.input}
                      required
                    >
                      {currencyOptions.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Network */}
                <div className="group">
                  <label className={`${GlobalStyles.inputLabel}`}>
                    Network
                  </label>
                  <div className="relative">
                    <select
                      value={formData.network}
                      onChange={(e) =>
                        setFormData({ ...formData, network: e.target.value })
                      }
                      className={GlobalStyles.input}
                      required
                    >
                      {getNetworkOptions().map((network) => (
                        <option key={network} value={network}>
                          {network}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="group">
                <label className={`${GlobalStyles.inputLabel}`}>
                  Additional Notes
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <textarea
                    value={formData.additional_notes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        additional_notes: e.target.value,
                      })
                    }
                    className={`${GlobalStyles.input} min-h-[100px] pt-3`}
                    placeholder="Provide any additional information about the incident..."
                  />
                </div>
              </div>

              {/* Two-Tier Fee Notice */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo}`}
              >
                <AlertTriangle className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Two-Tier Fee Structure
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    20% upfront fee + 10% success fee = 30% total of recovered
                    amount
                  </p>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="p-2 bg-gradient-to-r from-blue-500/5 to-blue-500/10 rounded-lg border border-blue-200/30 dark:border-blue-800/30">
                      <p className="text-xs font-medium text-blue-500">
                        Upfront Fee
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        20%
                      </p>
                    </div>
                    <div className="p-2 bg-gradient-to-r from-emerald-500/5 to-emerald-500/10 rounded-lg border border-emerald-200/30 dark:border-emerald-800/30">
                      <p className="text-xs font-medium text-emerald-500">
                        Success Fee
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        10%
                      </p>
                    </div>
                    <div className="p-2 bg-gradient-to-r from-purple-500/5 to-purple-500/10 rounded-lg border border-purple-200/30 dark:border-purple-800/30">
                      <p className="text-xs font-medium text-purple-500">
                        Total Fee
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} group/cancel`}
                >
                  <span>Cancel</span>
                  <X className="h-4 w-4 ml-1 opacity-0 group-hover/cancel:opacity-100 transition-opacity" />
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/submit disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      <Shield className="h-5 w-5 mr-2 transition-transform group-hover/submit:scale-110" />
                      <span>Submit Request</span>
                      <Sparkles className="h-5 w-5 ml-2 opacity-0 group-hover/submit:opacity-100 transition-opacity" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${GlobalStyles.card}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-gray-700 dark:text-gray-300">
              Filters
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/clear`}
            >
              <span>Clear All</span>
              <Sparkles className="h-3 w-3 ml-1 opacity-0 group-hover/clear:opacity-100 transition-opacity" />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} group/filter`}
            >
              <Filter className="h-3 w-3 mr-1" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Status Filter */}
          <div className="group">
            <label className={`${GlobalStyles.inputLabel}`}>Status</label>
            <div className="relative">
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="assessment_complete">Assessment Complete</option>
                <option value="recovery_plan_sent">Recovery Plan Sent</option>
                <option value="recovery_approved">Recovery Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Network Filter */}
          <div className="group">
            <label className={`${GlobalStyles.inputLabel}`}>Network</label>
            <div className="relative">
              <select
                value={filter.network}
                onChange={(e) =>
                  setFilter({ ...filter, network: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="">All Networks</option>
                <option value="Bitcoin">Bitcoin</option>
                <option value="Ethereum">Ethereum</option>
                <option value="Binance Smart Chain">Binance Smart Chain</option>
                <option value="Solana">Solana</option>
                <option value="Polygon">Polygon</option>
                <option value="Arbitrum">Arbitrum</option>
                <option value="TRON">TRON</option>
                <option value="Cardano">Cardano</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Search Filter */}
          <div className="group">
            <label className={`${GlobalStyles.inputLabel}`}>Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={filter.search}
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
                className={GlobalStyles.input}
              />
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="group">
              <label className={GlobalStyles.inputLabel}>Date From</label>
              <input
                type="date"
                value={filter.date_from}
                onChange={(e) =>
                  setFilter({ ...filter, date_from: e.target.value })
                }
                className={GlobalStyles.input}
              />
            </div>
            <div className="group">
              <label className={GlobalStyles.inputLabel}>Date To</label>
              <input
                type="date"
                value={filter.date_to}
                onChange={(e) =>
                  setFilter({ ...filter, date_to: e.target.value })
                }
                className={GlobalStyles.input}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recovery Cases */}
      <div className={`${GlobalStyles.card}`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              My Recovery Cases
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {filteredRequests.length} case
              {filteredRequests.length !== 1 ? "s" : ""} found
            </p>
          </div>
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <button
              onClick={() => dispatch(getRecoveryRequests({}) as any)}
              disabled={isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} group/refresh`}
            >
              <RefreshCw
                className={`h-3 w-3 mr-1 ${isLoading ? "animate-spin" : ""}`}
              />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {filteredRequests.length > 0 ? (
          <div className="overflow-x-auto rounded-xl">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Case #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Chat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Currency/Network
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Date Submitted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredRequests.map((request: RecoveryRequest) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 group/row"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {request.case_number || `CASE-${request.id}`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px] font-mono">
                        {request.wallet_address.slice(0, 16)}...
                        {request.wallet_address.slice(-8)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() =>
                          setShowChatModal({
                            isOpen: true,
                            caseId: request.id,
                            caseNumber:
                              request.case_number || `CASE-${request.id}`,
                          })
                        }
                        className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} flex items-center gap-1 group/chat relative`}
                        title="Open Chat"
                      >
                        <MessageSquare className="h-3 w-3" />
                        <span>Chat</span>
                        {request.unread_message_count &&
                          request.unread_message_count > 0 && (
                            <span className="absolute -top-1 -right-1 bg-gradient-to-r from-rose-500 to-rose-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center shadow border border-white dark:border-gray-900 animate-pulse">
                              {request.unread_message_count}
                            </span>
                          )}
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="font-bold text-gray-900 dark:text-white">
                        {formatCrypto(request.amount, request.currency)}
                      </div>
                      {request.estimated_recovery_amount && (
                        <div className="text-sm text-emerald-500">
                          Est:{" "}
                          {formatCrypto(
                            request.estimated_recovery_amount,
                            request.currency,
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-bold bg-gradient-to-r from-primary/10 to-primary/5 text-primary px-2 py-1 rounded-full w-fit">
                          {request.currency}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {request.network}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(request.status)}
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(request.status)}`}
                        >
                          {formatStatus(request.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(request.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {new Date(request.created_at).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openCaseDetails(request)}
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} flex items-center gap-1 group/details`}
                          title="View Details"
                        >
                          <Eye className="h-3 w-3" />
                          <span>Details</span>
                        </button>
                        {request.transaction_id && (
                          <a
                            href={getExplorerUrl(
                              request.network,
                              request.transaction_id,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} flex items-center gap-1 group/explorer`}
                            title="View on Explorer"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="relative mx-auto w-24 h-24 mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur-lg"></div>
              <div className="relative w-24 h-24 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                <FileText className="h-12 w-12 text-gray-400" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              No Recovery Cases
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Submit your first recovery request to get started with our
              professional recovery services
            </p>
            <button
              onClick={() => setShowForm(true)}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/start`}
            >
              <Plus className="h-5 w-5 mr-2 transition-transform group-hover/start:rotate-90" />
              <span>Start Recovery Case</span>
            </button>
          </div>
        )}
      </div>

      {/* Case Details Modal */}
      {showCaseDetails && selectedCase && (
        <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-xl">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={closeCaseDetails}
            />

            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden">
              {/* Background Glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

              {/* Modal Content */}
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        Case: {selectedCase.case_number}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(selectedCase.status)}`}
                        >
                          {formatStatus(selectedCase.status)}
                        </span>
                        {selectedCase.unread_message_count &&
                          selectedCase.unread_message_count > 0 && (
                            <span className="flex items-center gap-1 bg-gradient-to-r from-rose-500 to-rose-600 text-white text-xs px-2 py-1 rounded-full">
                              <Bell className="h-3 w-3" />
                              {selectedCase.unread_message_count} new
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeCaseDetails}
                    className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div
                  className="overflow-y-auto p-6"
                  style={{ maxHeight: "calc(90vh - 120px)" }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      {/* Case Info */}
                      <div className={`${GlobalStyles.card}`}>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <FileText className="h-4 w-4 text-primary" />
                          Case Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Network
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.network}
                            </p>
                          </div>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Currency
                            </p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {selectedCase.currency}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Transaction Info */}
                      <div className={`${GlobalStyles.card}`}>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Hash className="h-4 w-4 text-primary" />
                          Transaction Details
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                              Transaction ID
                            </p>
                            <div className="flex items-center justify-between group">
                              <p className="font-mono text-sm truncate max-w-[70%] text-gray-900 dark:text-white">
                                {selectedCase.transaction_id.slice(0, 16)}...
                                {selectedCase.transaction_id.slice(-16)}
                              </p>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      selectedCase.transaction_id,
                                      "Transaction ID",
                                    )
                                  }
                                  className="hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                                >
                                  {copiedField === "Transaction ID" ? (
                                    <Check className="h-4 w-4 text-emerald-500" />
                                  ) : (
                                    <Copy className="h-4 w-4 text-gray-400" />
                                  )}
                                </button>
                                <a
                                  href={getExplorerUrl(
                                    selectedCase.network,
                                    selectedCase.transaction_id,
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                                >
                                  <ExternalLink className="h-4 w-4 text-primary" />
                                </a>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Amount Lost
                              </p>
                              <p className="font-bold text-gray-900 dark:text-white">
                                {formatCrypto(
                                  selectedCase.amount,
                                  selectedCase.currency,
                                )}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                USD Value
                              </p>
                              <p className="font-bold text-gray-900 dark:text-white">
                                ${formatCurrency(selectedCase.amount * 45000)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      {/* Wallet Info */}
                      <div className={`${GlobalStyles.card}`}>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <WalletIcon className="h-4 w-4 text-primary" />
                          Wallet Information
                        </h4>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Wallet Address
                          </p>
                          <div className="flex items-center justify-between group">
                            <p className="font-mono text-sm truncate max-w-[80%] text-gray-900 dark:text-white">
                              {selectedCase.wallet_address.slice(0, 12)}...
                              {selectedCase.wallet_address.slice(-8)}
                            </p>
                            <button
                              onClick={() =>
                                handleCopy(
                                  selectedCase.wallet_address,
                                  "Wallet Address",
                                )
                              }
                              className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              {copiedField === "Wallet Address" ? (
                                <Check className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Recovery Info */}
                      <div className={`${GlobalStyles.card}`}>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-primary" />
                          Recovery Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-gradient-to-r from-emerald-500/5 to-emerald-500/10 rounded-lg border border-emerald-200 dark:border-emerald-800">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Estimated Recovery
                            </p>
                            <p className="font-bold text-emerald-500">
                              {selectedCase.estimated_recovery_amount
                                ? formatCrypto(
                                    selectedCase.estimated_recovery_amount,
                                    selectedCase.currency,
                                  )
                                : "—"}
                            </p>
                          </div>
                          <div className="p-3 bg-gradient-to-r from-blue-500/5 to-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-800">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Success Fee
                            </p>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {selectedCase.success_fee_percentage > 0
                                ? `${selectedCase.success_fee_percentage}%`
                                : "—"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Recovery Timeline */}
                      <div className={`${GlobalStyles.card}`}>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          Recovery Timeline
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              Submitted:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatDateTime(selectedCase.created_at)}
                            </span>
                          </div>
                          {selectedCase.assessment_completed_at && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500 dark:text-gray-400">
                                Assessment:
                              </span>
                              <span className="font-medium text-emerald-500">
                                {formatDateTime(
                                  selectedCase.assessment_completed_at,
                                )}
                              </span>
                            </div>
                          )}
                          {selectedCase.recovery_completed_at && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500 dark:text-gray-400">
                                Completed:
                              </span>
                              <span className="font-medium text-blue-500">
                                {formatDateTime(
                                  selectedCase.recovery_completed_at,
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal.isOpen && (
        <RecoveryChat
          recoveryId={showChatModal.caseId}
          caseNumber={showChatModal.caseNumber}
          isAdmin={false}
          onClose={() => {
            setShowChatModal({ isOpen: false, caseId: 0, caseNumber: "" });
            dispatch(getRecoveryRequests({}) as any);
            dispatch(getUnreadChatCount() as any);
          }}
          isOpen={showChatModal.isOpen}
        />
      )}
    </div>
  );
};

export default Recovery;
